import { Injectable, signal } from '@angular/core';

export type UiToastTone = 'success' | 'error';

export interface UiToastOptions {
  message: string;
  tone?: UiToastTone;
  durationMs?: number;
}

export interface UiToast {
  id: string;
  message: string;
  tone: UiToastTone;
}

function makeToastId(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

@Injectable({ providedIn: 'root' })
export class UiToastService {
  private readonly timers = new Map<string, number>();

  private readonly _toasts = signal<UiToast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private readonly _liveMessage = signal('');
  readonly liveMessage = this._liveMessage.asReadonly();

  show(opts: UiToastOptions): string {
    const message = (opts.message ?? '').trim();
    if (!message) return '';

    const durationMs = opts.durationMs ?? 5000;
    const existing = this._toasts().find((t) => t.message === message);
    if (existing) {
      const nextTone: UiToastTone = opts.tone ?? existing.tone;
      if (nextTone !== existing.tone) {
        this._toasts.update((list) =>
          list.map((t) => (t.id === existing.id ? { ...t, tone: nextTone } : t)),
        );
      }
      this.bumpLiveMessage(message);
      this.restartTimer(existing.id, durationMs);
      return existing.id;
    }

    const id = makeToastId();
    const tone: UiToastTone = opts.tone ?? 'success';
    const toast: UiToast = { id, message, tone };

    this._toasts.update((list) => [...list, toast]);
    this.bumpLiveMessage(message);
    this.restartTimer(id, durationMs);

    return id;
  }

  dismiss(id: string): void {
    const t = this.timers.get(id);
    if (t) {
      window.clearTimeout(t);
      this.timers.delete(id);
    }
    this._toasts.update((list) => list.filter((x) => x.id !== id));
  }

  clear(): void {
    for (const t of this.timers.values()) window.clearTimeout(t);
    this.timers.clear();
    this._toasts.set([]);
  }

  private restartTimer(id: string, durationMs: number): void {
    const existing = this.timers.get(id);
    if (existing) window.clearTimeout(existing);

    const timer = window.setTimeout(() => this.dismiss(id), durationMs);
    this.timers.set(id, timer);
  }

  private bumpLiveMessage(message: string): void {
    // If the message is identical, force a value change so aria-live can announce again.
    if (this._liveMessage() === message) {
      this._liveMessage.set('');
      queueMicrotask(() => this._liveMessage.set(message));
      return;
    }
    this._liveMessage.set(message);
  }
}
